import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { ChevronLeft, ChevronRight } from '@makyoapp/makyo-icon/icon'
import { SelectDropdown } from '@makyoapp/makyo-ui'

interface GridPaginationProps {
  pagination: {
    page: number
    perPage: number
  }
  onChange: (page: number, perPage: number) => void
  totalPages: number
  totalRows: number
  tableID: string
}

export const GridPagination = ({ pagination, onChange, totalPages, totalRows }: GridPaginationProps) => {
  const { t } = useTranslation()

  const firstRow = totalRows === 0 ? 1 : pagination.page * pagination.perPage + 1
  const lastRow = totalRows === 0 ? pagination.perPage : Math.min(totalRows, (pagination.page + 1) * pagination.perPage)

  const handleNextPage = () => {
    if (pagination.page < totalPages - 1) {
      onChange(pagination.page + 1, pagination.perPage)
    }
  }

  const handlePreviousPage = () => {
    if (pagination.page > 0) {
      onChange(pagination.page - 1, pagination.perPage)
    }
  }

  const handleFirstPage = () => {
    onChange(0, pagination.perPage)
  }

  const handleLastPage = () => {
    onChange(totalPages - 1, pagination.perPage)
  }

  const isPreviousButtonDisabled = pagination.page === 0 || totalRows === 0
  const isNextButtonDisabled = pagination.page === totalPages - 1 || totalPages === 0

  return (
    <div className='fixed bottom-0 left-0 right-0 flex items-center justify-end gap-4 text-zinc-700 dark:text-slate-300 bg-white dark:bg-slate-800 p-4 shadow-md h-[calc(60px)]'>
      <div className='flex items-center'>
        <span className='mr-2'>
          <Trans>Rows per page</Trans>:
        </span>
        <SelectDropdown
          value={pagination.perPage || 100}
          portalMenu
          placement={'top-start'}
          menuClassName='text-m-m text-zinc-700 dark:text-slate-300'
          menuFullWidth={false}
          onChange={(value: number) => onChange(pagination.page, value)}
          options={[10, 25, 50, 100]}
        />
      </div>

      <div className='flex items-center'>
        <span>
          {firstRow} to {lastRow} of {totalRows} {totalRows > 1 ? 'rows' : 'row'}
        </span>
      </div>

      <div className='flex items-center'>
        <span className='mr-2'>{t('PAGE_INFO', { page: pagination.page + 1, totalPages })}</span>

        <div className='flex gap-1'>
          <button
            className={clsx(
              'flex items-center justify-center p-2 rounded-sm transition-colors duration-300',
              isPreviousButtonDisabled
                ? 'text-zinc-400 dark:text-slate-600 cursor-default'
                : 'text-black dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700'
            )}
            onClick={handleFirstPage}
            disabled={isPreviousButtonDisabled}
          >
            <svg width='10' height='11' viewBox='0 0 10 11' xmlns='http://www.w3.org/2000/svg'>
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M1.5 0C1.22386 0 1 0.223858 1 0.5V10.5C1 10.7761 1.22386 11 1.5 11C1.77614 11 2 10.7761 2 10.5V0.5C2 0.223858 1.77614 0 1.5 0Z'
              />
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M9.85355 10.8536C10.0488 10.6583 10.0488 10.3417 9.85355 10.1464L5.20711 5.5L9.85355 0.853554C10.0488 0.658291 10.0488 0.341709 9.85355 0.146446C9.65829 -0.0488155 9.34171 -0.0488155 9.14645 0.146446L4.14645 5.14645C4.05268 5.24022 4 5.36739 4 5.5C4 5.63261 4.05268 5.75979 4.14645 5.85355L9.14645 10.8536C9.34171 11.0488 9.65829 11.0488 9.85355 10.8536Z'
              />
            </svg>
          </button>
          <button
            onClick={handlePreviousPage}
            disabled={isPreviousButtonDisabled}
            className={clsx(
              'flex items-center justify-center p-2 rounded-sm transition-colors duration-300',
              isPreviousButtonDisabled
                ? 'text-zinc-400 dark:text-slate-600 cursor-default'
                : 'text-black dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700'
            )}
          >
            <ChevronLeft />
          </button>
          <button
            onClick={handleNextPage}
            disabled={isNextButtonDisabled}
            className={clsx(
              'flex items-center justify-center p-2 rounded-sm transition-colors duration-300',
              isNextButtonDisabled
                ? 'text-zinc-400 dark:text-slate-600 cursor-default'
                : 'text-black dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700'
            )}
          >
            <ChevronRight />
          </button>
          <button
            onClick={handleLastPage}
            disabled={isNextButtonDisabled}
            className={clsx(
              'flex items-center justify-center p-2 rounded-sm transition-colors duration-300',
              isNextButtonDisabled
                ? 'text-zinc-400 dark:text-slate-600 cursor-default'
                : 'text-black dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700'
            )}
          >
            <svg width='10' height='11' viewBox='0 0 10 11' xmlns='http://www.w3.org/2000/svg'>
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M0.146447 10.8536C-0.0488156 10.6583 -0.0488156 10.3417 0.146447 10.1464L4.79289 5.5L0.146448 0.853554C-0.0488147 0.658291 -0.0488147 0.341709 0.146448 0.146446C0.34171 -0.0488155 0.658292 -0.0488155 0.853554 0.146446L5.85355 5.14645C5.94732 5.24022 6 5.36739 6 5.5C6 5.63261 5.94732 5.75979 5.85355 5.85355L0.853553 10.8536C0.658291 11.0488 0.341709 11.0488 0.146447 10.8536Z'
              />
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M8.5 0C8.77614 0 9 0.223858 9 0.5V10.5C9 10.7761 8.77614 11 8.5 11C8.22386 11 8 10.7761 8 10.5V0.5C8 0.223858 8.22386 0 8.5 0Z'
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
